import { ReactNode, Suspense, lazy, useCallback, useEffect } from 'react'
import {
  Route,
  Routes,
  Outlet,
  Navigate,
  useLocation,
  useNavigate,
  useSearchParams,
} from 'react-router-dom'
import toast, { Toaster } from 'react-hot-toast'

import { useAuth } from 'ctx/AuthContext'
import { useUI } from 'ctx/UIContext'

import LoadingScreen from 'components/layout/LoadingScreen'
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import 'react-tooltip/dist/react-tooltip.css'
import { useUser } from 'store/RootStore'
import Guides from 'pages/Common/Guides'
import useWhiteLabels from 'hooks/useWhiteLabels'
import { WHITELABEL_DATA } from 'Constants'
import { Helmet } from 'react-helmet'
import PublicGuides from 'pages/Common/PublicGuides'

const ForgotPassword = lazy(() => import('pages/Auth/ForgotPassword'))
const ResetPassword = lazy(() => import('pages/Auth/ResetPassword'))
const Login = lazy(() => import('pages/Auth/Login'))
const Signup = lazy(() => import('pages/Auth/Signup'))
const ConfirmSignup = lazy(() => import('pages/Auth/ConfirmSignup'))
const MainDashboard = lazy(() => import('pages/Dashboard'))
const AddSubmission = lazy(() => import('pages/Dashboard/AddSubmission'))
const EditSubmission = lazy(() => import('pages/Dashboard/EditSubmission'))
const MySubmissions = lazy(() => import('pages/Dashboard/MySubmissions'))
const Analytics = lazy(() => import('pages/Dashboard/Analytics'))
const AccountSettings = lazy(() => import('pages/Common/AccountSettings'))
const Unsubscribe = lazy(() => import('pages/Common/Unsubscribe'))

function App() {
  const ui = useUI()
  const auth = useAuth()
  const location = useLocation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { getUser } = useUser()
  const { whiteLabelData, setWhiteLabelData, setLoading } = useUI()
  const { getWhiteLabelData } = useWhiteLabels()

  const findWhiteLabelData = (appName: string) =>
    WHITELABEL_DATA.find((data) => data.appname === appName)

  useEffect(() => {
    const coupon = searchParams.get('affiliate')
    if (coupon) {
      localStorage.setItem('affiliate', coupon)
    }

    if (location.pathname === '/') {
      navigate('/my-submissions')
    }
  }, [searchParams, location, navigate])

  useEffect(() => {
    const fetchLabelData = async () => {
      try {
        setLoading(true)
        const res = await getWhiteLabelData()

        if (!res) {
          setWhiteLabelData({ wlabel: 'default' })
          return
        }

        if ('detail' in res) {
          toast.error('Failed to retrieve whitelabel data!')
          setWhiteLabelData({ wlabel: 'default' })
          return
        }

        setWhiteLabelData(res)
        const affiliateCode =
          res.wlabel || findWhiteLabelData(res.wlabel)?.appname || ''

        if (affiliateCode) {
          localStorage.setItem('affiliate', affiliateCode)
        }
      } catch (err) {
        console.log(err)
      } finally {
        setLoading(false)
      }
    }

    fetchLabelData()
  }, [])

  const PrivateRoute = useCallback(
    () => (auth?.user ? <Outlet /> : <Navigate to='/login' />),
    [auth?.user]
  )

  return (
    <>
      {whiteLabelData.wlabel !== 'default' && whiteLabelData.wlabel !== undefined ? (
        <Helmet>
          <meta charSet='utf-8' />
          <title>{`${
            `${whiteLabelData.company_name} | Dashboard` ||
            findWhiteLabelData(whiteLabelData.wlabel)?.title ||
            'Film Distribution | Dashboard'
          }`}</title>
          <meta
            name='description'
            content={`Our platform offers tools to help independent artists, labels, and distributors monetize their music and videos. Providing real-time analytics and earnings, ${whiteLabelData.app_name} allows creators to release their Singles, Albums and Videos worldwide 100% free to use.`}
          />
          <link rel='icon' href='/whitelabel/favicon.ico' data-react-helmet='true' />
          <meta name='viewport' content='width=device-width, initial-scale=1' />
          <meta name='theme-color' content='#000000' />
          <meta
            name='title'
            content={`${
              `${whiteLabelData.wlabel} | Dashboard` ||
              findWhiteLabelData(whiteLabelData.wlabel)?.title ||
              'Film Distribution | Dashboard'
            }`}
          />
          <meta property='og:type' content='website' />
          <meta
            name='description'
            content={`Our platform offers tools to help independent artists, labels, and distributors monetize their music and videos. Providing real-time analytics and earnings, ${whiteLabelData.app_name} allows creators to release their Singles, Albums and Videos worldwide 100% free to use.`}
          />
          <meta
            property='og:url'
            content={`https://${whiteLabelData.wlabel}.filmmakerdash.com/`}
          />
          <meta
            property='og:title'
            content={`${
              `${whiteLabelData.wlabel} | Dashboard` ||
              findWhiteLabelData(whiteLabelData.wlabel)?.title ||
              'Film Distribution | Dashboard'
            }`}
          />
          <meta
            property='og:description'
            content={`Our platform offers tools to help independent artists, labels, and distributors monetize their music and videos. Providing real-time analytics and earnings, ${whiteLabelData.app_name} allows creators to release their Singles, Albums and Videos worldwide 100% free to use.`}
          />

          <meta property='twitter:card' content='summary_large_image' />
          <meta
            property='twitter:url'
            content={`https://${whiteLabelData.wlabel}.filmmakerdash.com/`}
          />
          <meta
            property='twitter:title'
            content={`${
              `${whiteLabelData.wlabel} | Dashboard` ||
              findWhiteLabelData(whiteLabelData.wlabel)?.title ||
              'Film Distribution | Dashboard'
            }`}
          />
          <meta
            property='twitter:description'
            content={`Our platform offers tools to help independent artists, labels, and distributors monetize their music and videos. Providing real-time analytics and earnings, ${whiteLabelData.app_name} allows creators to release their Singles, Albums and Videos worldwide 100% free to use.`}
          />

          <link
            rel='apple-touch-icon'
            href='/whitelabel/apple-touch-icon.png'
            data-react-helmet='true'
          />

          <link
            rel='manifest'
            href='/whitelabel/manifest.json'
            data-react-helmet='true'
          />
          <link rel='icon' href={whiteLabelData.logo} data-react-helmet='true' />
        </Helmet>
      ) : whiteLabelData.wlabel === 'default' &&
        whiteLabelData.wlabel !== undefined ? (
        <Helmet>
          <meta charSet='utf-8' />
          <link rel='icon' href='/whitelabel/favicon.ico' data-react-helmet='true' />
          <meta name='viewport' content='width=device-width, initial-scale=1' />
          <meta name='theme-color' content='#000000' />
          <meta name='title' content='Film Distribution' />
          <meta property='og:type' content='website' />
          <meta
            name='description'
            content='Our platform offers tools to help independent artists, labels, and distributors monetize their music and videos. Providing real-time analytics and earnings, Film Distribution allows creators to release their Singles, Albums and Videos worldwide 100% free to use.'
          />
          <meta property='og:url' content='https://www.filmmakerdash.com/' />
          <meta property='og:title' content='Film Distribution' />
          <meta
            property='og:description'
            content='Our platform offers tools to help independent artists, labels, and distributors monetize their music and videos. Providing real-time analytics and earnings, Film Distribution allows creators to release their Singles, Albums and Videos worldwide 100% free to use.'
          />
          <meta property='twitter:card' content='summary_large_image' />
          <meta property='twitter:url' content='https://www.filmmakerdash.com/' />
          <meta property='twitter:title' content='Film Distribution' />
          <meta
            property='twitter:description'
            content='Our platform offers tools to help independent artists, labels, and distributors monetize their music and videos. Providing real-time analytics and earnings, Film Distribution allows creators to release their Singles, Albums and Videos worldwide 100% free to use.'
          />

          <link
            rel='apple-touch-icon'
            href='/whitelabel/apple-touch-icon.png'
            data-react-helmet='true'
          />

          <link
            rel='manifest'
            href='/whitelabel/manifest.json'
            data-react-helmet='true'
          />
          <link rel='icon' href='/whitelabel/favicon.ico' data-react-helmet='true' />
          <title>Film Distribution</title>
        </Helmet>
      ) : null}
      <Routes>
        <Route path='/login' element={<Login />} />
        <Route
          path='/signup'
          element={
            <SuspensedView>
              <Signup />
            </SuspensedView>
          }
        />
        <Route
          path='/forgot-password'
          element={
            <SuspensedView>
              <ForgotPassword />
            </SuspensedView>
          }
        />
        <Route
          path='/reset-password'
          element={
            <SuspensedView>
              <ResetPassword />
            </SuspensedView>
          }
        />
        <Route
          path='/confirm-signup'
          element={
            <SuspensedView>
              <ConfirmSignup />
            </SuspensedView>
          }
        />
        <Route
          path='/unsubscribe-confirm'
          element={
            <SuspensedView>
              <Unsubscribe />
            </SuspensedView>
          }
        />
        <Route
          path='public/guides'
          element={
            <SuspensedView>
              <PublicGuides />
            </SuspensedView>
          }
        />
        <Route path='/' element={<PrivateRoute />}>
          <Route
            path='/'
            element={
              <SuspensedView>
                <MainDashboard />
              </SuspensedView>
            }
          >
            <Route
              path='add-submission'
              element={
                <SuspensedView>
                  <AddSubmission />
                </SuspensedView>
              }
            />
            <Route
              path='submission/:type/:id'
              element={
                <SuspensedView>
                  <EditSubmission />
                </SuspensedView>
              }
            />
            <Route
              path='my-submissions'
              element={
                <SuspensedView>
                  <MySubmissions />
                </SuspensedView>
              }
            />

            <Route
              path='analytics'
              element={
                <SuspensedView>
                  <Analytics />
                </SuspensedView>
              }
            />

            <Route
              path='account-settings'
              element={
                <SuspensedView>
                  <AccountSettings />
                </SuspensedView>
              }
            />
            <Route
              path='guides'
              element={
                <SuspensedView>
                  <Guides />
                </SuspensedView>
              }
            />
          </Route>
        </Route>
      </Routes>
      <Toaster
        toastOptions={{
          duration: 6000,
          position: 'bottom-center',
          // Styling
          style: {
            borderRadius: '10px',
            color: '#eee',
          },
          className: 'rounded-full !bg-primary-purple',
        }}
      />
      {ui.isLoading ? <LoadingScreen isLoading /> : null}
      {/* <LoadingScreen isLoading /> */}
    </>
  )
}

const SuspensedView = ({ children }: { children: ReactNode }) => {
  return <Suspense fallback={<LoadingScreen isLoading />}>{children}</Suspense>
}

export default App
